import { privateClient } from "@/api/privateClient";
import { ACADEMICS_ENDPOINTS } from "../endpoint";
import type { AcademicGroup, CreateGroupPayload, AttachSubjectPayload, SetSubjectSchedulingPayload, Subject, ElectiveRosterEntry } from "../types";

export async function fetchAcademicStructure(institutionId: number): Promise<AcademicGroup[]> {
  const { data } = await privateClient.get(ACADEMICS_ENDPOINTS.STRUCTURE(institutionId));
  return data.tree ?? data;
}

export async function createAcademicGroup(
  institutionId: number,
  payload: CreateGroupPayload
): Promise<AcademicGroup> {
  const { data } = await privateClient.post(ACADEMICS_ENDPOINTS.GROUPS(institutionId), payload);
  return data.group ?? data;
}

export async function updateAcademicGroup(
  institutionId: number,
  groupId: number,
  payload: { name?: string; groupType?: string; displayOrder?: number }
): Promise<AcademicGroup> {
  const { data } = await privateClient.put(ACADEMICS_ENDPOINTS.GROUP(institutionId, groupId), payload);
  return data.group ?? data;
}

export async function moveAcademicGroup(institutionId: number, groupId: number, direction: "UP" | "DOWN"): Promise<{ moved: boolean }> {
  const { data } = await privateClient.post(ACADEMICS_ENDPOINTS.MOVE_GROUP(institutionId, groupId), { direction });
  return data;
}

/**
 * Refused server-side (409) if the node still has children (delete
 * bottom-up), or has ever had a student enrolled/elective-enrolled under
 * it — admission history is never silently orphaned.
 */
export async function deleteAcademicGroup(institutionId: number, groupId: number): Promise<void> {
  await privateClient.delete(ACADEMICS_ENDPOINTS.GROUP(institutionId, groupId));
}

export async function fetchOfferedSubjects(institutionId: number): Promise<Subject[]> {
  const { data } = await privateClient.get(ACADEMICS_ENDPOINTS.OFFERED_SUBJECTS(institutionId));
  return data.subjects ?? data;
}

/** Bulk-adds subjectIds from the master catalog to this institution's offered list. Returns the full offered list after the add. */
export async function offerSubjects(institutionId: number, subjectIds: number[]): Promise<Subject[]> {
  const { data } = await privateClient.post(ACADEMICS_ENDPOINTS.OFFERED_SUBJECTS(institutionId), { subjectIds });
  return data.subjects ?? data;
}

/** Refused server-side (409) if the subject is still attached to any academic group in this institution. */
export async function removeOfferedSubject(institutionId: number, subjectId: number): Promise<void> {
  await privateClient.delete(ACADEMICS_ENDPOINTS.OFFERED_SUBJECT(institutionId, subjectId));
}

export async function fetchSubjectCatalog(): Promise<Subject[]> {
  const { data } = await privateClient.get(ACADEMICS_ENDPOINTS.SUBJECTS());
  return data.subjects ?? data;
}

export async function attachSubjectToGroup(
  institutionId: number,
  payload: AttachSubjectPayload
) {
  const { data } = await privateClient.post(ACADEMICS_ENDPOINTS.GROUP_SUBJECTS(institutionId), payload);
  return data.attachment ?? data;
}

export async function detachSubjectFromGroup(institutionId: number, groupId: number, subjectId: number) {
  await privateClient.delete(ACADEMICS_ENDPOINTS.GROUP_SUBJECT(institutionId, groupId, subjectId));
}

export async function setSubjectScheduling(
  institutionId: number,
  payload: SetSubjectSchedulingPayload
) {
  const { data } = await privateClient.put(ACADEMICS_ENDPOINTS.SUBJECT_SCHEDULING(institutionId), payload);
  return data;
}

export async function fetchElectiveRoster(
  institutionId: number,
  groupId: number,
  subjectId: number,
  academicYearId: number
): Promise<ElectiveRosterEntry[]> {
  const { data } = await privateClient.get(ACADEMICS_ENDPOINTS.ELECTIVES(institutionId, groupId, subjectId), {
    params: { academicYearId },
  });
  return data.roster;
}

export async function enrollElective(
  institutionId: number,
  groupId: number,
  subjectId: number,
  studentId: number
) {
  const { data } = await privateClient.post(ACADEMICS_ENDPOINTS.ELECTIVES(institutionId, groupId, subjectId), { studentId });
  return data.enrollment;
}

export async function unenrollElective(
  institutionId: number,
  groupId: number,
  subjectId: number,
  enrollmentId: number
) {
  await privateClient.delete(ACADEMICS_ENDPOINTS.ELECTIVE(institutionId, groupId, subjectId, enrollmentId));
}

/**
 * Clones a whole Academic Structure Template's tree onto this institution's
 * academic_groups in one action — see the Super Admin's Academic Templates
 * page for building/managing templates themselves.
 */
export async function applyAcademicStructureTemplate(institutionId: number, templateId: number, force?: boolean) {
  const { data } = await privateClient.post(ACADEMICS_ENDPOINTS.APPLY_TEMPLATE(institutionId), { templateId, force });
  return data;
}

/** Sets (overwrites) this group's permanent class teacher — a teacher may only hold this for one group institution-wide at a time (backend rejects otherwise). */
export async function setClassTeacher(institutionId: number, groupId: number, staffId: number): Promise<{ academicGroupId: number; classTeacherStaffId: number }> {
  const { data } = await privateClient.put(ACADEMICS_ENDPOINTS.CLASS_TEACHER(institutionId, groupId), { staffId });
  return data;
}
export interface OwnTemplate { id: number; name: string; nodeCount: number }
export interface OwnTemplateNode { id: number; name: string; nodeType: string; children: OwnTemplateNode[] }

/** The institution's own templates and how many it may keep (the platform admin sets that limit). */
export async function fetchOwnTemplates(institutionId: number): Promise<{ templates: OwnTemplate[]; limit: number; used: number }> {
  const { data } = await privateClient.get(ACADEMICS_ENDPOINTS.OWN_TEMPLATES(institutionId));
  return data;
}

export async function createOwnTemplate(institutionId: number, name: string): Promise<OwnTemplate> {
  const { data } = await privateClient.post(ACADEMICS_ENDPOINTS.OWN_TEMPLATES(institutionId), { name });
  return data.template;
}

export async function deleteOwnTemplate(institutionId: number, templateId: number): Promise<void> {
  await privateClient.delete(ACADEMICS_ENDPOINTS.OWN_TEMPLATE(institutionId, templateId));
}

export async function fetchOwnTemplateTree(institutionId: number, templateId: number): Promise<OwnTemplateNode[]> {
  const { data } = await privateClient.get(`${ACADEMICS_ENDPOINTS.OWN_TEMPLATE(institutionId, templateId)}/structure`);
  return data.tree ?? [];
}

export async function addOwnTemplateNode(institutionId: number, templateId: number, node: { name: string; nodeType: string; parentId?: number | null }): Promise<void> {
  await privateClient.post(`${ACADEMICS_ENDPOINTS.OWN_TEMPLATE(institutionId, templateId)}/nodes`, node);
}

export async function removeOwnTemplateNode(institutionId: number, templateId: number, nodeId: number): Promise<void> {
  await privateClient.delete(`${ACADEMICS_ENDPOINTS.OWN_TEMPLATE(institutionId, templateId)}/nodes/${nodeId}`);
}

export async function applyOwnTemplate(institutionId: number, templateId: number): Promise<{ nodesCreated: number }> {
  const { data } = await privateClient.post(`${ACADEMICS_ENDPOINTS.OWN_TEMPLATE(institutionId, templateId)}/apply`);
  return data;
}
